import type { TimePeriod, MetricsResponse, SalesMetrics, RepairMetrics, InstallationMetrics, Employee, EmployeeStatus } from '../types';

const API_BASE = '/api';

// Fired when the server answers 401: the app re-checks the session and shows the PIN prompt.
export const AUTH_REQUIRED_EVENT = 'monitorics:auth-required';

// All API calls go through here: sends the session cookie and reports 401s.
async function apiFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const response = await fetch(url, { credentials: 'same-origin', ...init });
  if (response.status === 401) {
    window.dispatchEvent(new Event(AUTH_REQUIRED_EVENT));
  }
  return response;
}

async function fetchJson<T>(url: string): Promise<T> {
  const response = await apiFetch(url);
  if (!response.ok) {
    throw new Error(`API Error: ${response.status}`);
  }
  return response.json();
}

// Build URL with optional custom date params
function buildMetricsUrl(endpoint: string, period: TimePeriod, startDate?: string, endDate?: string): string {
  let url = `${API_BASE}/metrics/${endpoint}?period=${period}`;
  if (period === 'custom' && startDate && endDate) {
    url += `&startDate=${startDate}&endDate=${endDate}`;
  }
  return url;
}

export async function getSalesMetrics(period: TimePeriod, startDate?: string, endDate?: string): Promise<MetricsResponse<SalesMetrics>> {
  return fetchJson(buildMetricsUrl('sales', period, startDate, endDate));
}

export async function getRepairMetrics(period: TimePeriod, startDate?: string, endDate?: string): Promise<MetricsResponse<RepairMetrics>> {
  return fetchJson(buildMetricsUrl('repair', period, startDate, endDate));
}

export async function getInstallationMetrics(period: TimePeriod, startDate?: string, endDate?: string): Promise<MetricsResponse<InstallationMetrics>> {
  return fetchJson(buildMetricsUrl('installation', period, startDate, endDate));
}

export async function getAllMetrics(period: TimePeriod, startDate?: string, endDate?: string) {
  let url = `${API_BASE}/metrics/all?period=${period}`;
  if (period === 'custom' && startDate && endDate) {
    url += `&startDate=${startDate}&endDate=${endDate}`;
  }
  return fetchJson<{
    sales: MetricsResponse<SalesMetrics>;
    repair: MetricsResponse<RepairMetrics>;
    installation: MetricsResponse<InstallationMetrics>;
  }>(url);
}

// Team Management API
import type { Team } from '../types';

export async function getTeams(): Promise<Team[]> {
  return fetchJson(`${API_BASE}/teams`);
}

export async function getEmployees(): Promise<Employee[]> {
  return fetchJson(`${API_BASE}/employees`);
}

export async function updateEmployeeTeam(employeeId: string, teamId: string | null): Promise<Employee> {
  const response = await apiFetch(`${API_BASE}/employees/${employeeId}/team`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ teamId }),
  });
  if (!response.ok) {
    throw new Error(`API Error: ${response.status}`);
  }
  return response.json();
}

// Sync API
export interface SyncStatus {
  lastSuccessfulSync: string | null;
  recordCounts: {
    employees: number;
    customers: number;
    jobs: number;
    estimates: number;
  };
  autoSync?: {
    enabled: boolean;
    schedule: string;
    scheduleDescription: string;
  };
}

export interface SyncResult {
  success: boolean;
  duration: number;
  employees: number;
  customers: number;
  jobs: number;
  jobAssignments: number;
  estimates: number;
  estimateAssignments: number;
  error?: string;
}

export async function getSyncStatus(): Promise<SyncStatus> {
  return fetchJson(`${API_BASE}/sync/status`);
}

export async function runQuickSync(): Promise<SyncResult> {
  const response = await apiFetch(`${API_BASE}/sync/quick`, { method: 'POST' });
  if (!response.ok) {
    throw new Error(`Sync Error: ${response.status}`);
  }
  return response.json();
}

// Employee Visibility API
export async function updateEmployeeVisibility(employeeId: string, isHidden: boolean): Promise<Employee> {
  const response = await apiFetch(`${API_BASE}/employees/${employeeId}/visibility`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ isHidden }),
  });
  if (!response.ok) throw new Error(`API Error: ${response.status}`);
  return response.json();
}

// Employee Status API
export async function updateEmployeeStatus(employeeId: string, status: EmployeeStatus): Promise<Employee> {
  const response = await apiFetch(`${API_BASE}/employees/${employeeId}/status`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
  if (!response.ok) throw new Error(`API Error: ${response.status}`);
  return response.json();
}

// Employee Photo API
export async function uploadEmployeePhoto(employeeId: string, photo: string, contentType: string): Promise<Employee> {
  const response = await apiFetch(`${API_BASE}/employees/${employeeId}/photo`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ photo, contentType }),
  });
  if (!response.ok) throw new Error(`API Error: ${response.status}`);
  return response.json();
}

export async function deleteEmployeePhoto(employeeId: string): Promise<Employee> {
  const response = await apiFetch(`${API_BASE}/employees/${employeeId}/photo`, {
    method: 'DELETE',
  });
  if (!response.ok) throw new Error(`API Error: ${response.status}`);
  return response.json();
}

// Auth API (dashboard PIN, server-side session cookie)
export interface VerifyPinResult {
  valid: boolean;
  error?: string;
  retryAfterSec?: number;
}

export async function verifyPin(pin: string): Promise<VerifyPinResult> {
  // Plain fetch: a wrong PIN (401) must not trigger the global auth-required event.
  const response = await fetch(`${API_BASE}/auth/verify-pin`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pin }),
  });
  try {
    return await response.json();
  } catch {
    return { valid: false, error: `Server error (${response.status})` };
  }
}

export interface SessionState {
  authenticated: boolean;
  settingsUnlocked: boolean;
  expiresAt: string | null;
  configured: boolean;
}

export async function getSession(): Promise<SessionState> {
  const response = await fetch(`${API_BASE}/auth/session`, { credentials: 'same-origin', cache: 'no-store' });
  if (!response.ok) throw new Error(`API Error: ${response.status}`);
  return response.json();
}

export async function logout(): Promise<void> {
  await fetch(`${API_BASE}/auth/logout`, { method: 'POST', credentials: 'same-origin' });
}
